// src/pages/Moderation/UsersViolationsPage/ui/UsersFilters/UsersFilters.tsx
import React, { useEffect, useMemo, useState } from 'react';
import './UsersFilters.scss';

import { DebounceInput } from 'react-debounce-input';
import { GetFlagsResponse } from 'redux/reducers/moderation/types';
import { Select } from 'ui/NewSelect/Select';
import { UsersViolationsFilter } from '../types';
import { SortState } from './useUsersViolations';

type SelectOption<T extends string = string> = { id: T; label: string };

type Props = {
  value: UsersViolationsFilter;
  sort: SortState;
  restrictionOptions: SelectOption<string>[];
  onChange: (v: UsersViolationsFilter) => void;
  onSortChange: (s: SortState) => void;
  onReset: () => void;
  flags: GetFlagsResponse | null;
};

const ORDER_OPTIONS: SelectOption<'created_at' | 'username' | 'email'>[] = [
  { id: 'created_at', label: 'Created at' },
  { id: 'username', label: 'Username' },
  { id: 'email', label: 'Email' },
];

export const UsersFilters: React.FC<Props> = ({
  value,
  sort,
  flags,
  restrictionOptions,
  onChange,
  onSortChange,
  onReset,
}) => {
  const [local, setLocal] = useState<UsersViolationsFilter>(value);

  // ✅ якщо value змінюється ззовні — оновлюємо локальний стан
  useEffect(() => {
    setLocal(value);
  }, [value]);

  const update = (patch: Partial<UsersViolationsFilter>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange(next);
  };

  // --- Search (debounce) ---
  const [search, setSearch] = useState(value.search ?? '');
  const debounced = useDebounce(search, 300);

  useEffect(() => {
    update({ search: debounced });
  }, [debounced]);

  // --- Flags options ---
  const flagOptions: SelectOption<string>[] = useMemo(
    () =>
      (flags?.data ?? [])
        .filter((f) => f?.value != null)
        .map((f) => ({
          id: String(f.value),
          label: f.description || f.name || `Flag ${f.value}`,
        })),
    [flags],
  );

  return (
    <div className="uv-filters">
      {/* Complaint type / flag */}
      <Select
        options={flagOptions}
        value={typeof local.flag === 'number' ? String(local.flag) : undefined}
        placeholder="Complaint Type"
        onChange={(v: string) => update({ flag: v ? Number(v) : undefined })}
        className="uv-filters__field"
      />

      {/* Order by */}
      {/* <Select
        options={ORDER_OPTIONS}
        value={sort.orderBy}
        placeholder="Order by"
        onChange={(v: 'created_at' | 'username' | 'email') => onSortChange({ ...sort, orderBy: v })}
        className="uv-filters__field"
      /> */}

      {/* ASC */}
      <label className="uv-filters__toggle">
        <input
          type="checkbox"
          checked={!!sort.asc}
          onChange={(e) => onSortChange({ ...sort, asc: e.target.checked })}
        />
        ASC
      </label>

      {/* Search */}
      <div className="uv-filters__search">
        <DebounceInput
          className="uv-filters__search-input"
          placeholder="Search by id / username / email"
          debounceTimeout={1000}
          value={search || ''}
          onChange={(e) => setSearch(e.target.value)}
        />
        {!!search && (
          <button type="button" className="uv-filters__clear" onClick={() => setSearch('')}>
            ×
          </button>
        )}
      </div>

      {/* Reset */}
      <button type="button" className="uv-filters__reset" onClick={onReset}>
        Reset
      </button>
    </div>
  );
};

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}
